import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Autocomplete,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import EditIcon from "@mui/icons-material/EditOutlined"
import MailIcon from "@mui/icons-material/MailOutline"
import PhoneIcon from "@mui/icons-material/PhoneOutlined"
import ProjectIcon from "@mui/icons-material/FolderSpecialOutlined"

import { clientApi, projectApi, userApi } from "../../api"
import { usePermissions } from "../../permissions"
import { LoadingScreen } from "../../components/LoadingState"
import PersonSelect from "../../components/PersonSelect"
import UserAvatar from "../../components/UserAvatar"
import { formatDate } from "../../utils/format"
import { rise } from "../../motion"

/**
 * Suggested industries, merged with what has been typed before.
 *
 * Suggestions and not a closed list: a practice's clients are its own, and the
 * box takes anything. These are only here so the first few clients do not each
 * invent a spelling of "Trucking".
 */
export const COMMON_INDUSTRIES = [
  "Appraisals",
  "Beauty & Personal Care",
  "Construction",
  "Healthcare",
  "Insurance",
  "Marketing & Media",
  "Professional Services",
  "Real Estate",
  "Restaurants & Hospitality",
  "Retail",
  "Talent & Entertainment",
  "Trucking & Logistics",
  "Wireless Retail",
]

const STATUS_LABEL = { active: "Active", suspended: "Suspended", archived: "Archived" }

/**
 * A client, without leaving the page you were reading.
 *
 * ── The same shape as the project dialog, on purpose ────────────────────────
 *
 * A client's name turns up on the Clients table, on every report row and every
 * project, and the question is the same each time: who are they, who looks
 * after them here, how do I reach them, how much is going on. Navigating to
 * answer it loses whatever raised the question. This answers it over the top,
 * and the projects inside it go one step further when that is what was wanted.
 *
 * ── Why editing lives here ──────────────────────────────────────────────────
 *
 * There was nowhere else to edit a client — the API took changes and the app
 * offered none, so a typo in a client's name stayed. Edit turns the same panel
 * into a form, in place, for the people allowed to; everybody else reads it.
 * The ID and the wireless carrier are shown and never offered for edit: both
 * decide the client's database, and moving either is a migration.
 *
 * @param {string|null} clientId Null closes it.
 */
export default function ClientDetailDialog({ clientId, onClose, onChanged }) {
  const navigate = useNavigate()
  const { can, isBackOffice } = usePermissions()
  const mayEdit = isBackOffice && can("clients", "update")

  const [client, setClient] = useState(null)
  const [projects, setProjects] = useState([])
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [staff, setStaff] = useState([])
  const [industries, setIndustries] = useState([])

  const load = useCallback(async () => {
    if (!clientId) return
    setLoading(true)
    setError(null)
    try {
      const [detail, projectData, peopleData] = await Promise.all([
        clientApi.get(clientId),
        projectApi.list({ client_id: clientId, limit: 50 }).catch(() => ({ projects: [] })),
        userApi.list({ client_id: clientId, limit: 50 }).catch(() => ({ users: [] })),
      ])
      setClient(detail.organization)
      setProjects(projectData.projects || [])
      setPeople((peopleData.users || []).filter((person) => person.active))
    } catch (err) {
      setError(err.userMessage || "This client could not be loaded.")
      setClient(null)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    // Cleared on open, so the previous client's figures never sit under the
    // next one's name while it loads.
    setClient(null)
    setProjects([])
    setPeople([])
    setEditing(false)
    load()
  }, [load])

  const startEditing = () => {
    setForm({
      label: client.label || "",
      industry: client.industry || "",
      owner_user_id: client.owner_user_id ? String(client.owner_user_id) : "",
      status: client.status,
      contact_name: client.contact_name || "",
      contact_email: client.contact_email || "",
      contact_phone: client.contact_phone || "",
      description: client.description || "",
    })
    setEditing(true)
    // The owner is somebody on our side, so the picker offers the back office.
    userApi
      .list({ client_id: "0", limit: 100 })
      .then((data) => setStaff((data.users || []).filter((person) => person.active)))
      .catch(() => setStaff([]))
    clientApi
      .industries()
      .then((data) => setIndustries(data.industries || []))
      .catch(() => setIndustries([]))
  }

  const industryOptions = useMemo(
    () => [...new Set([...industries, ...COMMON_INDUSTRIES])].sort((a, b) => a.localeCompare(b)),
    [industries],
  )

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await clientApi.update(client.id, {
        ...form,
        owner_user_id: form.owner_user_id ? Number(form.owner_user_id) : null,
        // The back office has no status to change; the server refuses it.
        ...(client.id === "0" ? { status: undefined } : {}),
      })
      setEditing(false)
      await load()
      onChanged?.()
    } catch (err) {
      setError(err.userMessage || "Those changes could not be saved.")
    } finally {
      setSaving(false)
    }
  }

  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }))
  const openProjects = projects.filter((project) => project.status !== "archived")

  return (
    <Dialog open={Boolean(clientId)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {client?.label || "Client"}
        <Typography variant="caption" color="text.secondary" display="block">
          {client
            ? [client.industry, `ID ${client.id}`, client.carrier_label ? `${client.carrier_label} dealer` : null]
                .filter(Boolean)
                .join(" · ")
            : "Loading…"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !client ? <LoadingScreen minHeight={220} /> : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {client && editing ? (
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField label="Name" value={form.label} onChange={set("label")} fullWidth required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={industryOptions}
                value={form.industry}
                onInputChange={(_event, value) => setForm((current) => ({ ...current, industry: value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Industry" helperText="Pick one, or type your own" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PersonSelect
                people={staff}
                value={form.owner_user_id}
                onChange={(value) => setForm((current) => ({ ...current, owner_user_id: value || "" }))}
                label="Owner"
                emptyLabel="Nobody yet"
                helperText="Who on our side looks after them"
              />
            </Grid>
            {client.id !== "0" ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Status" value={form.status} onChange={set("status")} fullWidth>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </TextField>
              </Grid>
            ) : null}
            <Grid size={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  PRIMARY CONTACT
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Contact name" value={form.contact_name} onChange={set("contact_name")} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Phone" value={form.contact_phone} onChange={set("contact_phone")} fullWidth />
            </Grid>
            <Grid size={12}>
              <TextField label="Email" type="email" value={form.contact_email} onChange={set("contact_email")} fullWidth />
            </Grid>
            <Grid size={12}>
              <TextField label="Notes" value={form.description} onChange={set("description")} fullWidth multiline minRows={2} />
            </Grid>
          </Grid>
        ) : null}

        {client && !editing ? (
          <Stack spacing={2}>
            {client.description ? (
              <Typography variant="body2" color="text.secondary">
                {client.description}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Fact label="Status" value={STATUS_LABEL[client.status] || client.status} />
              <Fact label="Industry" value={client.industry || "Not set"} muted={!client.industry} />
              <Fact
                label="Owner"
                value={
                  client.owner_name ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <UserAvatar name={client.owner_name} avatarKey={client.owner_avatar} size={20} tooltip={false} />
                      <span>{client.owner_name}</span>
                    </Stack>
                  ) : (
                    "Nobody yet"
                  )
                }
                muted={!client.owner_name}
              />
              <Fact label="Client since" value={formatDate(client.created_at) || "—"} />
            </Stack>

            {/* The three numbers somebody opens a client to ask about. */}
            <Stack direction="row" spacing={1.5}>
              <Kpi index={0} label="People" value={client.user_count} />
              <Kpi index={1} label="Projects" value={client.project_count} />
              <Kpi index={2} label="Open tasks" value={client.open_task_count} />
            </Stack>

            <Divider />

            <Box>
              <Typography variant="body2" fontWeight={650} sx={{ mb: 1 }}>
                Primary contact
              </Typography>
              {client.contact_name || client.contact_email || client.contact_phone ? (
                <Stack spacing={0.5}>
                  {client.contact_name ? <Typography variant="body2">{client.contact_name}</Typography> : null}
                  {client.contact_email ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <MailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Link href={`mailto:${client.contact_email}`} variant="body2">
                        {client.contact_email}
                      </Link>
                    </Stack>
                  ) : null}
                  {client.contact_phone ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Link href={`tel:${client.contact_phone}`} variant="body2">
                        {client.contact_phone}
                      </Link>
                    </Stack>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nobody recorded.{mayEdit ? " Edit to add who to talk to." : ""}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={650} sx={{ flex: 1 }}>
                  Projects
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {openProjects.length} open
                </Typography>
              </Stack>
              {openProjects.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No projects yet.
                </Typography>
              ) : (
                <Stack spacing={0.25}>
                  {openProjects.slice(0, 6).map((project, index) => {
                    const total = Number(project.task_count) || 0
                    const done = total - (Number(project.open_task_count) || 0)
                    return (
                      <Box
                        key={project.id}
                        component="button"
                        type="button"
                        onClick={() => {
                          onClose()
                          navigate(`/projects/${project.id}`)
                        }}
                        sx={{
                          appearance: "none",
                          border: 0,
                          background: "none",
                          font: "inherit",
                          color: "text.primary",
                          textAlign: "left",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1,
                          py: 0.6,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          ...rise(index),
                        }}
                      >
                        <ProjectIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                        <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                          {project.code}
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                          {project.name}
                        </Typography>
                        {total > 0 ? (
                          <Chip size="small" variant="outlined" label={`${done}/${total} done`} sx={{ height: 18, fontSize: 10 }} />
                        ) : null}
                      </Box>
                    )
                  })}
                  {openProjects.length > 6 ? (
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => {
                        onClose()
                        navigate(`/projects?client_id=${client.id}`)
                      }}
                      sx={{ alignSelf: "flex-start", mt: 0.5 }}
                    >
                      {openProjects.length - 6} more
                    </Link>
                  ) : null}
                </Stack>
              )}
            </Box>

            {people.length > 0 ? (
              <>
                <Divider />
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="body2" fontWeight={650}>
                    Their people
                  </Typography>
                  <AvatarGroup max={8} sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 11 } }}>
                    {people.map((person) => (
                      <UserAvatar key={person.id} name={person.name || person.username} avatarKey={person.avatar_key} size={28} />
                    ))}
                  </AvatarGroup>
                </Stack>
              </>
            ) : null}

            {client.carrier_label ? (
              <Typography variant="caption" color="text.secondary">
                Wireless carrier: {client.carrier_label}. Set when the client was added, because it decides their
                reconciliation tables, and not changeable here.
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        {editing ? (
          <>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => setEditing(false)} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={save} disabled={saving || !form.label.trim()}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </>
        ) : (
          <>
            {mayEdit ? (
              <Button startIcon={<EditIcon />} onClick={startEditing} disabled={!client}>
                Edit
              </Button>
            ) : null}
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

/** A label above its value. */
function Fact({ label, value, muted = false }) {
  return (
    <Box sx={{ minWidth: 92 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography component="div" variant="body2" fontWeight={600} color={muted ? "text.secondary" : "text.primary"}>
        {value}
      </Typography>
    </Box>
  )
}

/** One figure in a small tile. */
function Kpi({ label, value, index }) {
  return (
    <Box
      sx={{
        flex: 1,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        ...rise(index),
      }}
    >
      <Typography variant="h3" component="p">
        {Number(value) || 0}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}
